// components/header/Header.tsx - Enhanced version with optional Redux support
import React, { useState, useEffect, useCallback } from "react";
import { Link, matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import LockIcon from "@mui/icons-material/Lock";
import { IconButton } from "@mui/material";
import { MenuItem } from "@mui/material";
import { Menu } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { ListItemText } from "@mui/material";
import { Divider } from "@mui/material";
import { ListItemIcon } from "@mui/material";

// import { Pathname } from "../../pathname"; // Removed - should be passed as props
import Typography from "../typography/typography";
import logo, { images } from "../../assets/images";
import Select from "../select/select";
import modules from "../../constants/modules"; // Removed - should be passed as props
import Avatar from "../avatar/avatar";
import { useAuth } from '@erpsquad/common/runtime';
import ConfirmPopUp from "../confirm-modal/confirm-modal";
import formatText from "../../utils/format-text";
import { useLanguage } from '@erpsquad/common/runtime';
import { Eye } from "../icons";
import ResetPasswordModal from "../reset-password-modal/reset-password-modal";
import NotificationMenu from "./components/notification-menu/notification-menu";
import "./header.scss";
import { getApiConfig } from "@/utils";
import { Pathname } from "@/constants";
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { fetchLanguages } from '../../redux/slices';
interface ILanguage {
  label: string;
  code: string;
  direction: string;
}

interface IModule {
  label: any;
  link: string;
  sidebar: string;
  icon: string;
  permission: string;
  bg: string;
  connectedRoutes: string[];
  translationModule: string;
}

// Enhanced props interface
interface HeaderEnhancedProps {
  isDropdownShow?: boolean;

  // Language props (alternative to Redux)
  languages?: ILanguage[];
  currentLanguage?: ILanguage;
  languagesLoading?: boolean;
  languageError?: string;
  onLanguageChange?: (languageCode: string) => void;
  onFetchLanguages?: () => Promise<void>;

  // Redux integration (optional)
  useRedux?: boolean;
}

export function HeaderEnhanced(props: HeaderEnhancedProps): React.ReactElement {
  const {
    isDropdownShow,

    // Non-Redux props
    languages: propLanguages = [],
    currentLanguage: propCurrentLanguage,
    languagesLoading: propLanguagesLoading = false,
    languageError: propLanguageError = "",
    onLanguageChange,
    onFetchLanguages,

    // Redux integration
    useRedux = false,
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { logout, user } = useAuth();

  // Conditional Redux usage
  let reduxLanguageState = {
    languages: [],
    currentLanguage: { label: 'English', code: 'en', direction: 'ltr' },
    isLoading: false,
    error: '',
    isFallbackActive: false,
    isRtl: false
  };
  let dispatch: any = null;
  let reduxActions: any = {};

  if (useRedux) {
    try {
      // Redux hooks and actions (statically imported)

      const headerState = useAppSelector((store: any) => store.header || {});
      dispatch = useAppDispatch();
      reduxActions = { fetchLanguages };

      reduxLanguageState = {
        languages: headerState.languagesData?.data || [],
        currentLanguage: headerState.currentLanguage || reduxLanguageState.currentLanguage,
        isLoading: headerState.languagesData?.isLoading || false,
        error: headerState.languagesData?.error || '',
        isFallbackActive: false, // This would need to be computed
        isRtl: headerState.currentLanguage?.direction === 'rtl'
      };
    } catch (error) {
      console.warn("Redux not available for Header, falling back to prop-based data");
    }
  }

  // Use Redux data if available, otherwise use props or useLanguage hook
  const {
    languages: hookLanguages,
    currentLanguage: hookCurrentLanguage,
    changeLanguage: hookChangeLanguage,
    isLoading: hookLanguagesLoading,
    error: hookLanguageError,
    isFallbackActive: hookIsFallbackActive,
    fetchLanguages: hookFetchLanguages,
    isRtl: hookIsRtl
  } = useLanguage();
  const config = getApiConfig()

  // Determine which data source to use
  const languages = useRedux ? reduxLanguageState.languages : (propLanguages.length > 0 ? propLanguages : hookLanguages);
  const currentLanguage = useRedux ? reduxLanguageState.currentLanguage : (propCurrentLanguage || hookCurrentLanguage);
  const languagesLoading = useRedux ? reduxLanguageState.isLoading : (propLanguagesLoading || hookLanguagesLoading);
  const languageContextError = useRedux ? reduxLanguageState.error : (propLanguageError || hookLanguageError);
  const isFallbackActive = useRedux ? reduxLanguageState.isFallbackActive : hookIsFallbackActive;
  const isRtl = useRedux ? reduxLanguageState.isRtl : hookIsRtl;

  const { pathname } = location;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [logoutConfirmation, setLogoutConfirmation] = useState<boolean>(false);
  const [selectedModule, setSelectedModule] = useState<IModule | null>(null);
  const [erp_modules, setModules] = useState<IModule[]>([]);
  const [localLanguageError, setLocalLanguageError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const u_data = localStorage.getItem("_u_data") || undefined;
  const userData = u_data ? JSON.parse(u_data) : "";
  const isMenuOpen = Boolean(anchorEl);

  const handleLogoutConfirmation = () =>
    setLogoutConfirmation(!logoutConfirmation);

  const checkMatchedRoutes = useCallback(
    (paths: string[] | string) => {
      const shouldMatch: { path: string }[] = Array.isArray(paths)
        ? paths.map((path: string) => ({ path }))
        : [{ path: paths }];

      return Boolean(matchRoutes(shouldMatch, location));
    },
    [location],
  );

  const handleChangePasswordView = () => {
    setShowModal(true);
  }

  useEffect(() => {
    const moduleName = erp_modules.find(
      (module: IModule) =>
        module?.link?.toLowerCase() === pathname.toLowerCase() ||
        checkMatchedRoutes(module.connectedRoutes),
    );

    if (moduleName) {
      setSelectedModule(moduleName);
    }
  }, [pathname, erp_modules, checkMatchedRoutes]);

  useEffect(() => {
    setModules(modules(t));
  }, [t]);

  // Enhanced language change handler
  const onChangeLang = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang_code = e.target.value;

    // Clear any previous errors
    setLocalLanguageError(null);

    // Validate the language exists before changing
    if (!languages || languages.length === 0) {
      const errorMsg = t('header.language_not_available') || 'Languages not available';
      setLocalLanguageError(errorMsg);
      return;
    }

    const languageExists = languages.some((lang: ILanguage) => lang.code === lang_code);

    if (languageExists) {
      try {
        if (useRedux) {
          // Use Redux action (would need to be implemented in the slice)
          // dispatch(setCurrentLanguage({ code: lang_code, ... }));
        } else if (onLanguageChange) {
          onLanguageChange(lang_code);
        } else {
          hookChangeLanguage(lang_code);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to change language';
        setLocalLanguageError(errorMsg);
      }
    } else {
      const errorMsg = t('header.language_not_found', { language: lang_code }) ||
        `Selected language "${lang_code}" is not available`;
      setLocalLanguageError(errorMsg);
    }
  };

  // Retry language loading
  const handleRetryLanguages = async () => {
    setLocalLanguageError(null);
    try {
      if (useRedux && dispatch && reduxActions.fetchLanguages) {
        await dispatch(reduxActions.fetchLanguages());
      } else if (onFetchLanguages) {
        await onFetchLanguages();
      } else {
        await hookFetchLanguages();
      }
    } catch (error) {
      const errorMsg = 'Failed to reload languages';
      setLocalLanguageError(errorMsg);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: any) => {
    const selectedLink = event.target.value;
    setTimeout(() => {
      navigate(selectedLink);
    }, 300);
  };

  const handleBackButton = () => {
    window.history.back();
  };

  // Get current language display value with fallback indication
  const getCurrentLanguageDisplay = (): string => {
    if (!currentLanguage) return t('header.select_language') || 'Select Language';

    // Show fallback indicator for better UX
    if (isFallbackActive || languageContextError) {
      return `${currentLanguage.label}`;
    }

    return currentLanguage.label;
  };

  // Create English fallback for consistent display
  const createEnglishFallback = (): ILanguage => ({
    code: 'en',
    label: 'English',
    direction: 'ltr'
  });

  // Get languages for dropdown - ensure English is always available
  const getLanguagesForDropdown = (): ILanguage[] => {
    if (!languages || languages.length === 0) {
      // If no languages available, show only English
      return [createEnglishFallback()];
    }

    // Ensure English is in the list
    const hasEnglish = languages.some(lang => lang.code === 'en');
    if (!hasEnglish) {
      return [createEnglishFallback(), ...languages];
    }

    return languages;
  };

  // Render language selector based on different states
  const renderLanguageSelector = () => {
    const availableLanguages = getLanguagesForDropdown();
    const displayValue = currentLanguage?.code || 'en'; // Default to English

    // Loading state
    if (languagesLoading) {
      return (
        <Box className="language-loading">
          <Typography variant="body2" color="text.secondary">
            {'Loading...'}
          </Typography>
        </Box>
      );
    }

    // Always show dropdown with at least English available
    return (
      <Select
        onChange={onChangeLang}
        size="small"
        value={displayValue}
        renderValue={() => getCurrentLanguageDisplay()}
        error={!!localLanguageError}
        className={isFallbackActive || languageContextError ? 'language-fallback-active' : ''}
      >
        {availableLanguages.map(({ code, label }: ILanguage) => (
          <MenuItem key={code} value={code}>
            <Typography
              type="s4"
              weight="medium"
              color="theme.secondary.1000"
            >
              {label}
            </Typography>
          </MenuItem>
        ))}

        {/* Show retry option if there's an error */}
        {languageContextError && (
          <MenuItem onClick={handleRetryLanguages} style={{ fontStyle: 'italic' }}>
            <Typography
              type="s4"
              weight="medium"
              color="theme.secondary.500"
            >
              🔄 {t('header.retry_languages') || 'Retry loading languages'}
            </Typography>
          </MenuItem>
        )}
      </Select>
    );
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      className="main-profile-menu"
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem className="user-assigned">
        <span>{t('common.assigned_roles') || 'Assigned roles'}</span>
      </MenuItem>
      <MenuItem className="user-data-wrap" value={userData.role_id}>
        <ListItemIcon>
          <Avatar size={27} alt={userData.role_name} />
        </ListItemIcon>
        <ListItemText className="size15">
          {userData.role_name}
        </ListItemText>
        <Typography variant="body2" color="text.secondary">
          <img src={images.tickicon} alt="Active" />
        </Typography>
      </MenuItem>
      <Divider />
      <MenuItem
        className="user-assigned"
        onClick={() => {
          handleChangePasswordView();
          setAnchorEl(null);
        }}
      >
        <ListItemIcon>
          <Eye fontSize="medium" width={20} height={20} htmlColor="#292D32" />
        </ListItemIcon>
        <ListItemText className="size15">{t("common.change_password")}</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem
        className="menu-item-color"
        onClick={() => {
          handleLogoutConfirmation();
          setAnchorEl(null);
        }}
      >
        <ListItemIcon>
          <img src={images.logout} alt="logout" />
        </ListItemIcon>
        <ListItemText className="size15">{t("common.logout")}</ListItemText>
      </MenuItem>
    </Menu>
  );

  const appLogo = getApiConfig().appLogo || images.logo

  return (
    <>
      <Box className="main-box">
        {!isDropdownShow && (
          <Link to={user ? Pathname.DASHBOARD : Pathname.LOGIN}>
            <img src={appLogo} alt="logo" height={55}/>
          </Link>
        )}
        <Box className="second-box">
          {isDropdownShow && (
            <>
              <IconButton onClick={handleBackButton}>
                <KeyboardBackspaceIcon
                  className={isRtl ? "arrow-pointer_rtl" : "arrow-pointer"}
                />
              </IconButton>
              <Box width={250}>
                <Select
                  fullWidth
                  placeholder="Placeholder"
                  size={"small"}
                  value={selectedModule?.link || ""}
                  onChange={handleChange}
                  className="select-box"
                  variant="outlined"
                  renderValue={(value: string) => (
                    <>
                      {selectedModule ? (
                        <Box className="selected-menu-box">
                          <img
                            src={selectedModule.icon}
                            alt={selectedModule.label}
                          />
                          {selectedModule.label}
                        </Box>
                      ) : (
                        value
                      )}
                    </>
                  )}
                >
                  {erp_modules.map((module: any, index: number) => (
                    <MenuItem
                      className="select-box-menu-item"
                      value={module.link}
                      key={index}
                    >
                      <img src={module.icon} alt={module.label} />
                      {module.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </>
          )}
        </Box>
        <Box className="header-right-wrap">
          {/* Language Selector with Enhanced Fallback Support */}
          <Box className="language-selector-wrapper">
            {renderLanguageSelector()}
          </Box>

          <NotificationMenu />
          <IconButton
            size="small"
            aria-label="help"
            className="p-0"
          >
            <img src={logo.question} alt="question icon" />
          </IconButton>
          <img src={logo.vdivider} alt="divider icon" />
          {localStorage.getItem("_tid") ? (
            <Box className="user-profile-box" onClick={handleProfileMenuOpen}>
              <Avatar
                size={32}
                src={user?.profile_image ? `${config?.s3BucketUrl}${user?.profile_image}` : undefined}
                alt={formatText(user?.full_name)}
                className="avatar-class"
              />
              <div>
                <Typography className="profile-name">
                  {user?.full_name}
                </Typography>
                <Typography className="user-auth">
                  {user?.role_name}
                </Typography>
              </div>
            </Box>
          ) : (
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              component={Link}
              to={Pathname.LOGIN}
              color="inherit"
            >
              <LockIcon className="icon-btn" />
            </IconButton>
          )}
        </Box>
        {renderMenu}
      </Box>

      {/* Logout Confirmation Dialog */}
      <ConfirmPopUp
        open={logoutConfirmation}
        title={t("common.logout")}
        description={t('common.logout_confirmation') || "Are you sure, you want to logout?"}
        onConfirm={() => logout()}
        onClose={handleLogoutConfirmation}
        fullWidth
        maxWidth="xs"
        buttonTexts={{
          confirm: t("common.logout"),
          cancel: t("common.cancel"),
        }}
      />

      <ResetPasswordModal showModal={showModal} setShowModal={setShowModal} />
    </>
  );
}

export default HeaderEnhanced;